/**
 * Retry Strategy Configuration and Builder
 * Defines retry behavior for resilient API communication
 */

/**
 * Configuration for retry logic
 * Controls exponential backoff and which errors are retryable
 */
export interface RetryStrategy {
  /** Maximum number of attempts (including initial request) */
  maxAttempts: number;

  /** Initial delay in milliseconds before first retry */
  initialDelayMs: number;

  /** Maximum delay in milliseconds between retries */
  maxDelayMs: number;

  /** Multiplier for exponential backoff (e.g., 2 = double delay each retry) */
  backoffMultiplier: number;

  /** Random jitter factor (0-1) to add randomness and prevent thundering herd */
  jitterFactor: number;

  /** HTTP status codes that should trigger a retry */
  retryableStatusCodes: number[];
}

/**
 * Default retry strategy suitable for most API calls
 * - 3 total attempts (1 initial + 2 retries)
 * - Starts at 100ms, exponential backoff up to 10s
 * - Retries transient errors: timeout, rate limit, server errors
 */
export const DEFAULT_RETRY_STRATEGY: RetryStrategy = {
  maxAttempts: 3,
  initialDelayMs: 100,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  jitterFactor: 0.1,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Aggressive retry strategy for critical operations
 * - 5 total attempts
 * - Longer backoff (up to 30s)
 * - Includes server errors and timeouts
 */
export const AGGRESSIVE_RETRY_STRATEGY: RetryStrategy = {
  maxAttempts: 5,
  initialDelayMs: 100,
  maxDelayMs: 30000,
  backoffMultiplier: 2.5,
  jitterFactor: 0.15,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Minimal retry strategy for read-only operations
 * - 2 total attempts (1 initial + 1 retry)
 * - Short delays
 * - Only retries severe transient errors
 */
export const MINIMAL_RETRY_STRATEGY: RetryStrategy = {
  maxAttempts: 2,
  initialDelayMs: 50,
  maxDelayMs: 5000,
  backoffMultiplier: 2,
  jitterFactor: 0.05,
  retryableStatusCodes: [408, 503, 504],
};

/**
 * Builder for creating custom retry strategies
 * Fluent API for configuration
 */
export class RetryStrategyBuilder {
  private strategy: RetryStrategy;

  constructor(baseStrategy: RetryStrategy = DEFAULT_RETRY_STRATEGY) {
    // Deep copy the base strategy
    this.strategy = JSON.parse(JSON.stringify(baseStrategy));
  }

  /**
   * Set maximum number of attempts
   * @param maxAttempts Number of attempts (must be >= 1)
   * @returns This builder for chaining
   */
  maxAttempts(maxAttempts: number): RetryStrategyBuilder {
    if (maxAttempts < 1) {
      throw new Error('maxAttempts must be at least 1');
    }
    this.strategy.maxAttempts = maxAttempts;
    return this;
  }

  /**
   * Set initial delay
   * @param delayMs Initial delay in milliseconds (must be >= 0)
   * @returns This builder for chaining
   */
  initialDelay(delayMs: number): RetryStrategyBuilder {
    if (delayMs < 0) {
      throw new Error('initialDelayMs must be non-negative');
    }
    this.strategy.initialDelayMs = delayMs;
    return this;
  }

  /**
   * Set maximum delay cap
   * @param delayMs Maximum delay in milliseconds (must be >= initialDelayMs)
   * @returns This builder for chaining
   */
  maxDelay(delayMs: number): RetryStrategyBuilder {
    if (delayMs < this.strategy.initialDelayMs) {
      throw new Error('maxDelayMs must be >= initialDelayMs');
    }
    this.strategy.maxDelayMs = delayMs;
    return this;
  }

  /**
   * Set exponential backoff multiplier
   * @param multiplier Backoff multiplier (must be > 1)
   * @returns This builder for chaining
   */
  backoffMultiplier(multiplier: number): RetryStrategyBuilder {
    if (multiplier <= 1) {
      throw new Error('backoffMultiplier must be > 1');
    }
    this.strategy.backoffMultiplier = multiplier;
    return this;
  }

  /**
   * Set jitter factor
   * @param factor Jitter factor (0-1, where 1 = 100% random jitter)
   * @returns This builder for chaining
   */
  jitterFactor(factor: number): RetryStrategyBuilder {
    if (factor < 0 || factor > 1) {
      throw new Error('jitterFactor must be between 0 and 1');
    }
    this.strategy.jitterFactor = factor;
    return this;
  }

  /**
   * Set retryable HTTP status codes
   * @param codes Array of HTTP status codes to retry
   * @returns This builder for chaining
   */
  retryableStatusCodes(codes: number[]): RetryStrategyBuilder {
    if (!Array.isArray(codes) || codes.length === 0) {
      throw new Error('retryableStatusCodes must be a non-empty array');
    }
    this.strategy.retryableStatusCodes = [...codes];
    return this;
  }

  /**
   * Add additional status codes to retryable list
   * @param codes HTTP status codes to add
   * @returns This builder for chaining
   */
  addRetryableStatusCodes(...codes: number[]): RetryStrategyBuilder {
    this.strategy.retryableStatusCodes = [
      ...new Set([...this.strategy.retryableStatusCodes, ...codes]),
    ];
    return this;
  }

  /**
   * Build the final retry strategy
   * @returns Immutable retry strategy
   */
  build(): Readonly<RetryStrategy> {
    // Validate the built strategy
    if (this.strategy.initialDelayMs > this.strategy.maxDelayMs) {
      throw new Error('initialDelayMs cannot be greater than maxDelayMs');
    }

    return Object.freeze({ ...this.strategy });
  }
}

/**
 * Determine if an error should be retried
 * Considers HTTP status codes and error types
 *
 * @param error Error object (fetch error, timeout, etc.)
 * @param statusCode HTTP status code (if available)
 * @param strategy Retry strategy containing retryable codes
 * @returns True if error should be retried
 */
export function isRetryableError(
  error: unknown,
  statusCode?: number,
  strategy?: RetryStrategy
): boolean {
  const retryableCodes = strategy?.retryableStatusCodes || DEFAULT_RETRY_STRATEGY.retryableStatusCodes;

  // Check HTTP status code
  if (statusCode && retryableCodes.includes(statusCode)) {
    return true;
  }

  // Check error type
  if (error instanceof Error) {
    // Timeout/AbortError is retryable
    if (error.name === 'AbortError') {
      return true;
    }

    // Network errors are retryable
    if (error instanceof TypeError && error.message.includes('network')) {
      return true;
    }

    // Generic network failures
    const message = error.message.toLowerCase();
    if (
      message.includes('failed to fetch') ||
      message.includes('network error') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('econnreset') ||
      message.includes('etimedout')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Calculate delay for exponential backoff with jitter
 *
 * Formula:
 * baseDelay = min(initialDelay * (multiplier ^ attemptNumber), maxDelay)
 * jitter = baseDelay * random(0, jitterFactor)
 * delay = baseDelay + jitter
 *
 * @param attemptNumber Zero-based attempt number (0 = first retry)
 * @param strategy Retry strategy configuration
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(
  attemptNumber: number,
  strategy: RetryStrategy
): number {
  if (attemptNumber < 0) {
    throw new Error('attemptNumber must be non-negative');
  }

  // Calculate base delay with exponential backoff
  const exponentialDelay =
    strategy.initialDelayMs * Math.pow(strategy.backoffMultiplier, attemptNumber);
  const baseDelay = Math.min(exponentialDelay, strategy.maxDelayMs);

  // Add random jitter to prevent thundering herd
  const jitterRange = baseDelay * strategy.jitterFactor;
  const jitter = Math.random() * jitterRange;

  return Math.floor(baseDelay + jitter);
}

/**
 * Validate retry strategy configuration
 * @param strategy Strategy to validate
 * @throws Error if strategy is invalid
 */
export function validateRetryStrategy(strategy: RetryStrategy): void {
  if (!strategy) {
    throw new Error('Retry strategy is required');
  }

  if (strategy.maxAttempts < 1) {
    throw new Error('maxAttempts must be at least 1');
  }

  if (strategy.initialDelayMs < 0) {
    throw new Error('initialDelayMs must be non-negative');
  }

  if (strategy.maxDelayMs < strategy.initialDelayMs) {
    throw new Error('maxDelayMs must be >= initialDelayMs');
  }

  if (strategy.backoffMultiplier <= 1) {
    throw new Error('backoffMultiplier must be > 1');
  }

  if (strategy.jitterFactor < 0 || strategy.jitterFactor > 1) {
    throw new Error('jitterFactor must be between 0 and 1');
  }

  if (!Array.isArray(strategy.retryableStatusCodes) || strategy.retryableStatusCodes.length === 0) {
    throw new Error('retryableStatusCodes must be a non-empty array');
  }
}
